import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MarkdownContent } from './MarkdownContent';

function reasoningPhase(text: string): string {
  const sample = text.slice(-500).toLowerCase();
  if (/final polish|memfinalkan/.test(sample)) return 'Memfinalkan jawaban';
  if (/draft|menyusun|drafting|polish/.test(sample)) return 'Menyusun jawaban';
  if (/refin|review|memeriksa/.test(sample)) return 'Memeriksa jawaban';
  if (/strateg|determine|analyze|analisa|analisis/.test(sample)) return 'Menganalisis permintaan';
  return 'Memproses permintaan';
}

type ReasoningPanelProps = {
  content: string;
  streaming: boolean;
};

export function ReasoningPanel({ content, streaming }: ReasoningPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const phase = useMemo(() => reasoningPhase(content), [content]);

  useEffect(() => {
    if (!expanded) return;
    const body = bodyRef.current;
    if (!body || !followRef.current) return;
    body.scrollTop = body.scrollHeight;
  }, [content, expanded, streaming]);

  const toggleExpanded = () => {
    setExpanded((value) => {
      const next = !value;
      if (next) followRef.current = true;
      return next;
    });
  };

  return (
    <div className="reasoning-panel">
      <button
        type="button"
        className="reasoning-panel__toggle"
        onClick={toggleExpanded}
        aria-expanded={expanded}
      >
        <span className="reasoning-panel__label">
          {streaming && (
            <span className="flex gap-1" aria-hidden="true">
              <i className="thinking-dot" />
              <i className="thinking-dot" />
              <i className="thinking-dot" />
            </span>
          )}
          <span>{phase}</span>
          {!expanded && streaming && <span className="reasoning-panel__hint">Ketuk untuk lihat detail</span>}
        </span>
        {expanded ? <ChevronUp className="size-4 shrink-0 text-muted" /> : <ChevronDown className="size-4 shrink-0 text-muted" />}
      </button>

      {expanded && (
        <div
          ref={bodyRef}
          className="reasoning-panel__body"
          onScroll={(event) => {
            const body = event.currentTarget;
            followRef.current = body.scrollHeight - body.scrollTop - body.clientHeight < 24;
          }}
        >
          <div className="reasoning-panel__markdown">
            <MarkdownContent value={content} />
            {streaming && <span className="stream-cursor reasoning-panel__cursor" aria-hidden="true" />}
          </div>
        </div>
      )}
    </div>
  );
}
